// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    title: string;
    entries: { key: string; label: string; count?: number; on?: boolean }[];
    onOpen: () => void;
    disabled?: boolean;
};

/**
 * One group as information rather than inputs: what has been answered, and a way to answer more.
 * Both groups draw the same card, so they pass it their entries instead of repeating the markup.
 *
 * A row is its label with the answer at the far end, because the labels are what you scan down and
 * the answers are what you stop for. A count is the figure itself; a feature is a tick or a cross
 * in a circle, the same two marks the group uses everywhere else.
 */
export default function FieldsGroup({ title, entries, onOpen, disabled }: Props) {
    const t = useTranslation();

    return (
        <section className="fields-group">
            <header className="fields-group__head">
                <span className="fields-group__title">{title}</span>
                <button
                    type="button"
                    className="side-form__add-beside"
                    onClick={onOpen}
                    disabled={disabled}
                    aria-label={t.common.add}
                    title={t.common.add}
                >
                    <Icon name="add" size={16} />
                </button>
            </header>

            {!entries.length && <p className="fields-group__empty">{t.properties.emptyDetails}</p>}

            {entries.length > 0 && (
                <ul className="fields-group__list">
                    {entries.map((entry) => (
                        <li className="fields-group__row" key={entry.key}>
                            <span className="fields-group__row-label">{entry.label}</span>

                            {entry.count !== undefined ? (
                                <span className="fields-group__number">{entry.count}</span>
                            ) : (
                                <span
                                    className={`fields-group__mark fields-group__mark--${entry.on ? "yes" : "no"}`}
                                    title={entry.on ? t.properties.yes : t.properties.no}
                                >
                                    <Icon name={entry.on ? "check" : "close"} size={13} />
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
